import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { fabric } from 'fabric';

import { Modal } from '@/common';
import { TextOverlayControls, TextOverlaySidebar } from '@/components/common';
import { Button, Spinner } from '@/components/generic';
import {
  BUTTON_TYPE,
  ButtonConfig,
  ButtonVariant
} from '@/components/generic/Button/button-config';
import { DetailsPageLayout } from '@/components/layouts';
import { queryClient } from '@/configuration/react-query';
import { CANVAS_OBJECT_TYPE, DEFAULT_CANVAS_JSON, UPLOAD_PICKER_TYPES } from '@/constants';
import { AssetScope, AssetType, PRIVATE_PAGE_ROUTES } from '@/enums';
import {
  checkIsTypingActive,
  generateFileName,
  getCanvasFromRef,
  getCanvasJson,
  getFullHDImageFromCanvas,
  loadFonts,
  removeActiveObjects
} from '@/helpers';
import { showToast, ToastType } from '@/helpers/toast';
import { useFileUpload } from '@/hooks';
import { Events as Calendar } from '@/resources';
import { SidebarAssets } from '@/resources/icons/sidebar';
import {
  useCreateTextOverlayMutation,
  useGetEventByIdQuery,
  useGetTextOverlayByIdQuery,
  useUpdateTextOverlayCanvasMutation
} from '@/services';

import { TextOverlayCanvas } from './TextOverlayCanvas';

export const CreateEditTextOverlay = () => {
  const [t] = useTranslation('common');
  const { eventId, textOverlayId, organizationId } = useParams();
  const [searchParams] = useSearchParams();
  const isEditPage = Boolean(textOverlayId);
  const navigate = useNavigate();

  const [selectedObjects, setSelectedObjects] = useState<Record<string, fabric.Object>>({});
  const [allCanvasObjects, setAllCanvasObjects] = useState<fabric.Object[]>([]);
  const [isFontsLoading, setIsFontsLoading] = useState(false);
  const [isCanvasReady, setIsCanvasReady] = useState(false);
  const [hasCanvasChanged, setHasCanvasChanged] = useState(false);
  const [hasColorChanged, setHasColorChanged] = useState(false);

  const [activeTextOverlayId, setActiveTextOverlayId] = useState<number | null>(null);
  const [activeAssetId, setActiveAssetId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isOpenSaveChoiceModal, setIsOpenSaveChoiceModal] = useState(false);

  const { mutateAsync: createOverlay } = useCreateTextOverlayMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('textOverlayCreatedSuccessfully'));
    },
    onError: () => {
      showToast(ToastType.ERROR, t('somethingWentWrong'));
    }
  });
  const { mutateAsync: updateTextOverlayCanvas } = useUpdateTextOverlayCanvasMutation(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('textOverlayUpdatedSuccessfully'));
    },
    onError: () => {
      showToast(ToastType.ERROR, t('somethingWentWrong'));
    }
  });

  const { data: eventData } = useGetEventByIdQuery(Number(eventId));
  const hasResourceContext = Boolean(organizationId) || Boolean(eventData);
  const { data: textOverlayData, isLoading: isTextOverlayLoading } = useGetTextOverlayByIdQuery(
    Number(textOverlayId)
  );

  const { uploadFiles } = useFileUpload({
    eventId,
    organizationId: organizationId ?? eventData?.data?.organization?.id,
    pickerTypes: UPLOAD_PICKER_TYPES.IMAGE,
    isPublic: false,
    assetScope: organizationId ? AssetScope.ORGANIZATION : AssetScope.PRIVATE,
    uploadAssetType: AssetType.TEXT_OVERLAY,
    activeAssetId,
    showSummaryToast: false
  });

  const { uploadFiles: uploadFilesAsNewCopy } = useFileUpload({
    eventId,
    organizationId: organizationId ?? eventData?.data?.organization?.id,
    pickerTypes: UPLOAD_PICKER_TYPES.IMAGE,
    isPublic: false,
    assetScope: organizationId ? AssetScope.ORGANIZATION : AssetScope.PRIVATE,
    uploadAssetType: AssetType.TEXT_OVERLAY,
    showSummaryToast: false
  });

  const canvasRef = useRef<fabric.Canvas | null>(null);

  useEffect(() => {
    handleFontLoading();
  }, []);

  useEffect(() => {
    if (!canvasRef.current || !isCanvasReady || isFontsLoading) {
      return;
    }
    const canvas = canvasRef.current;

    const handleCanvasChange = () => {
      if (canvas.getObjects().length === 0 && !hasColorChanged) {
        setHasCanvasChanged(false);
      } else {
        setHasCanvasChanged(true);
      }
    };

    const handleTextChange = (e: fabric.IEvent<Event>) => {
      handleCanvasChange();

      const target = e.target;
      const targetId = target?.data?.id;

      if (target && targetId) {
        setSelectedObjects((currentSelectedObjects) => ({
          ...currentSelectedObjects,
          [targetId]: target
        }));
      }
    };

    canvas.on('selection:created', handleSelection);
    canvas.on('selection:cleared', handleDeselection);
    canvas.on('selection:updated', handleSelection);

    canvas.on('object:removed', handleCanvasChange);
    canvas.on('object:modified', handleCanvasChange);
    canvas.on('path:created', handleCanvasChange);
    canvas.on('text:changed', handleTextChange);

    return () => {
      canvas.off('selection:created', handleSelection);
      canvas.off('selection:cleared', handleDeselection);
      canvas.off('selection:updated', handleSelection);
      canvas.off('object:removed', handleCanvasChange);
      canvas.off('object:modified', handleCanvasChange);
      canvas.off('path:created', handleCanvasChange);
      canvas.off('text:changed', handleTextChange);
    };
  }, [isCanvasReady, isFontsLoading, hasColorChanged]);

  useEffect(() => {
    document.addEventListener('keydown', handleDeleteShortcut);
    return () => {
      document.removeEventListener('keydown', handleDeleteShortcut);
    };
  }, []);

  useEffect(() => {
    if (textOverlayData?.data) {
      setActiveTextOverlayId(textOverlayData?.data.id);
      setActiveAssetId(textOverlayData?.data.asset.id);
    }
  }, [textOverlayData]);

  const handleSelection = (e: fabric.IEvent<Event>) => {
    if (e?.selected) {
      const tempSelectedObjects: Record<string, fabric.Object> = {};
      e.selected.forEach((obj) => {
        const id = obj?.data?.id;
        if (id) {
          tempSelectedObjects[id] = obj;
        }
      });
      setSelectedObjects(tempSelectedObjects);
    }
  };

  const handleDeselection = () => {
    setSelectedObjects({});
  };

  const handleDeleteShortcut = (e: KeyboardEvent) => {
    if (e.key === 'Delete') {
      if (checkIsTypingActive()) {
        return;
      }
      removeObjectFromCanvas();
      e.preventDefault();
    }
  };

  const removeObjectFromCanvas = () => {
    const canvas = getCanvasFromRef(canvasRef);
    if (!canvas) {
      throw new Error('Canvas not initialized');
    }
    removeActiveObjects(canvas);
    setSelectedObjects({});
    setAllCanvasObjects(canvas.getObjects());
  };

  const handleFontLoading = async () => {
    setIsFontsLoading(true);
    await loadFonts();
    setIsFontsLoading(false);
  };

  const navigateAfterSave = () => {
    const returnTo = searchParams.get('returnTo');
    const returnDeviceScheduleId = searchParams.get('deviceScheduleId');

    if (returnTo === 'digital-signage' && returnDeviceScheduleId) {
      navigate(
        `${PRIVATE_PAGE_ROUTES.DIGITAL_SIGNAGE_SCREENS}?deviceScheduleId=${returnDeviceScheduleId}`
      );
      return;
    }

    if (organizationId) {
      navigate(PRIVATE_PAGE_ROUTES.ORGANIZATION_ASSETS);
    } else {
      navigate(PRIVATE_PAGE_ROUTES.SINGLE_EVENT.replace(':eventId', `${eventData?.data?.id}`));
    }
  };

  const saveTextOverlay = async (saveAsNewCopy: boolean) => {
    try {
      setIsSaving(true);
      const canvas = getCanvasFromRef(canvasRef);
      const canvasJson = getCanvasJson(canvas);

      const fileName = generateFileName('text-overlay', 'png');
      const originalFile = await getFullHDImageFromCanvas(canvas as fabric.Canvas, fileName);
      const uploadResult = await (saveAsNewCopy ? uploadFilesAsNewCopy : uploadFiles)([
        originalFile
      ]);
      const originalFileAsset = uploadResult.assets?.[0];

      if (!originalFileAsset) {
        throw new Error('File creation failed');
      }

      if (isEditPage && !saveAsNewCopy && activeTextOverlayId && activeAssetId) {
        await updateTextOverlayCanvas({
          id: activeTextOverlayId,
          canvasJson: canvasJson
        });
      } else {
        const textOverlay = await createOverlay({
          assetId: originalFileAsset.id as number,
          canvasJson: canvasJson,
          ...(eventId ? { eventId: Number(eventId) } : {}),
          ...(organizationId ? { organizationId: Number(organizationId) } : {})
        });
        setActiveTextOverlayId(textOverlay.data.id);
        setActiveAssetId(originalFileAsset.id as number);
      }

      setHasCanvasChanged(false);
    } catch (error) {
      showToast(ToastType.ERROR, t('somethingWentWrong'));
    } finally {
      setIsSaving(false);
      navigateAfterSave();
    }
  };

  const handleSaveChanges = async () => {
    if (isEditPage) {
      setIsOpenSaveChoiceModal(true);
      return;
    }

    await saveTextOverlay(false);
  };

  const handleCanvasLoad = (loadedCanvas: fabric.Canvas) => {
    setIsCanvasReady(true);

    const allObjects = loadedCanvas.getObjects();
    const textObjects = allObjects.filter((obj) => obj.type === CANVAS_OBJECT_TYPE.TEXTBOX);

    textObjects.forEach((textObj) => {
      loadedCanvas.bringToFront(textObj);
    });

    if (textObjects.length > 0) {
      loadedCanvas.renderAll();
    }

    setAllCanvasObjects(loadedCanvas.getObjects());
  };

  const breadcrumbItems = [
    {
      label: eventId ? eventData?.data?.name : t('organizationAssets'),
      url: eventId
        ? PRIVATE_PAGE_ROUTES.SINGLE_EVENT.replace(':eventId', `${eventData?.data?.id}`)
        : PRIVATE_PAGE_ROUTES.ORGANIZATION_ASSETS
    },
    {
      label: t('textOverlay'),
      url: eventId
        ? PRIVATE_PAGE_ROUTES.SINGLE_EVENT.replace(':eventId', `${eventData?.data?.id}`)
        : PRIVATE_PAGE_ROUTES.ORGANIZATION_ASSETS
    },
    { label: isEditPage ? t('editOverlayText') : t('createOverlayText') }
  ];

  const buttonConfig: ButtonConfig[] = [
    {
      type: BUTTON_TYPE.BUTTON,
      label: t('saveChanges'),
      variant: ButtonVariant.PRIMARY,
      handleOnClick: () => handleSaveChanges(),
      isDisabled: isFontsLoading || isSaving || !hasCanvasChanged
    }
  ];

  return (
    <DetailsPageLayout
      isLoading={false}
      icon={
        organizationId ? (
          <SidebarAssets className='text-lg text-primary-blue' />
        ) : (
          <Calendar className='stroke-[0.5] text-lg text-primary-blue' />
        )
      }
      iconPath={
        organizationId ? PRIVATE_PAGE_ROUTES.ORGANIZATION_ASSETS : PRIVATE_PAGE_ROUTES.EVENTS
      }
      path={breadcrumbItems}
      headerButtons={buttonConfig}
      childrenClassName='h-full'
      title={isEditPage ? t('editOverlayText') : t('createOverlayText')}>
      {((isEditPage && isTextOverlayLoading) || isFontsLoading) && (
        <div className='flex flex-1 items-center justify-center'>
          <Spinner />
        </div>
      )}
      {!isFontsLoading && (!isEditPage || (!isTextOverlayLoading && hasResourceContext)) && (
        <div className='mt-4 flex w-full flex-1 flex-col md:mt-0'>
          <div className='relative flex min-h-0 w-full flex-1 flex-col gap-8 md:flex-row'>
            <div className='relative size-full min-h-0 flex-1'>
              <TextOverlayCanvas
                ref={canvasRef}
                contentJson={
                  isEditPage
                    ? (textOverlayData?.data?.canvasJson as string)
                    : JSON.stringify(DEFAULT_CANVAS_JSON)
                }
                onCanvasLoad={handleCanvasLoad}
              />
            </div>
            <div className='flex min-h-0 w-full flex-1 flex-col'>
              <TextOverlaySidebar
                ref={canvasRef}
                selectedObjects={selectedObjects}
                setSelectedObjects={setSelectedObjects}
                allCanvasObjects={allCanvasObjects}
                setAllCanvasObjects={setAllCanvasObjects}
                setHasCanvasChanged={setHasCanvasChanged}
                setHasColorChanged={setHasColorChanged}
                isEditPage={isEditPage}
              />
            </div>
          </div>
          <TextOverlayControls
            ref={canvasRef}
            selectedObjects={selectedObjects}
            setSelectedObjects={setSelectedObjects}
            setHasCanvasChanged={setHasCanvasChanged}
          />
        </div>
      )}
      {isOpenSaveChoiceModal && (
        <Modal
          isOpen={isOpenSaveChoiceModal}
          onClose={() => setIsOpenSaveChoiceModal(false)}
          label={t('saveChanges')}
          modalClasses='h-auto min-w-[320px]'>
          <div className='flex flex-col items-center justify-center gap-4 p-6'>
            <p className='text-center text-sm text-gray-600'>
              {t('replaceImage')}{' '}
              <b>{textOverlayData?.data?.asset?.assetName ?? t('textOverlay')}</b>
              {'?'}
            </p>
            <div className='flex flex-row gap-3'>
              <Button
                type={'button'}
                variant={ButtonVariant.PRIMARY}
                className='px-4'
                disabled={isSaving}
                handleOnClick={async () => {
                  setIsOpenSaveChoiceModal(false);
                  await saveTextOverlay(false);
                }}>
                {t('replaceOriginal')}
              </Button>
              <Button
                type={'button'}
                variant={ButtonVariant.SECONDARY}
                className='px-4'
                disabled={isSaving}
                handleOnClick={async () => {
                  setIsOpenSaveChoiceModal(false);
                  await saveTextOverlay(true);
                }}>
                {t('saveAsNewCopy')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </DetailsPageLayout>
  );
};
